import {ChangeDetectionStrategy, Component, inject, OnInit} from '@angular/core';
import {EntityInfoComponent, isDocumentEntity} from '|shared';
import {ApiSettlementService, BulkTriggerEventUpdateDto, DocumentDto, FileDto} from '|api/document';
import {EntityType, InternalNotificationKey} from '|api/commons';
import {TranslateService} from '@ngx-translate/core';
import {
  ButtonComponent,
  FormButtonsComponent,
  WaitingDirective,
  LoadingIndicatorService
} from '@icz/angular-essentials';
import {DialogService, DialogSeverity, injectModalData, injectModalRef} from '@icz/angular-modal';
import {
  FormFieldComponent,
  IczFormContainerDirective,
  IczFormControl,
  IczFormGroup,
  IczValidators
} from '@icz/angular-form-elements';
import {ReactiveFormsModule} from '@angular/forms';
import {AsyncPipe} from '@angular/common';
import {
  CheckUnsavedFormDialogService,
  IczFormSubmitDirective,
  IFormGroupCheckable
} from '@icz/angular-application-core';
import {DocumentToastService, DocumentToastType, FileToastService, FileToastType} from '|ui-essl-core';

export interface EditRetentionYearDialogData {
  editCheckYear: boolean,
  selectedObjects: (DocumentDto | FileDto)[],
}

@Component({
  selector: 'icz-edit-retention-year-dialog',
  templateUrl: './edit-retention-year-dialog.component.html',
  styleUrls: ['./edit-retention-year-dialog.component.scss'],
  providers: [
    CheckUnsavedFormDialogService
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, IczFormContainerDirective, WaitingDirective, EntityInfoComponent, FormFieldComponent, FormButtonsComponent, ButtonComponent, IczFormSubmitDirective, AsyncPipe]
})
export class EditRetentionYearDialogComponent implements OnInit, IFormGroupCheckable {

  protected loadingService = inject(LoadingIndicatorService);
  protected modalRef = injectModalRef<Nullable<boolean>>();
  protected checkUnsavedService = inject(CheckUnsavedFormDialogService);
  private documentToastService = inject(DocumentToastService);
  private fileToastService = inject(FileToastService);
  private translateService = inject(TranslateService);
  private apiSettlementService = inject(ApiSettlementService);
  protected data = injectModalData<EditRetentionYearDialogData>();
  private dialogService = inject(DialogService);

  form = new IczFormGroup({
    yearOfRetentionPeriodStart: new IczFormControl<Nullable<number>>(null),
    triggerEventCheckYear: new IczFormControl<Nullable<number>>(null, [IczValidators.min((new Date()).getFullYear())]),
  });

  formGroupsToCheck!: string[];

  isBulkOperation = false;

  ngOnInit() {
    this.loadingService.doLoading(
      this.apiSettlementService.settlementCanUserEditEventYear(),
      this
    ).subscribe(isChangeAllowed => {
      if (!isChangeAllowed) {
        this.dialogService.openSimpleDialog({
          title: this.data.editCheckYear ? 'Editace lhůty kontroly události' : 'Editace roku spouštěcí události',
          content: [
            {text: this.data.editCheckYear ? 'Editace lhůty kontroly události je povolena jen pro funkční místo s rolí Posuzovatel skartace.' : 'Editace roku spouštěcí události je povolena jen pro funkční místo s rolí Posuzovatel skartace.'},
          ],
          severity: DialogSeverity.WARNING
        }).subscribe(_ => {
          this.modalRef.close(null);
        });
      }
    });
    this.checkUnsavedService.addUnsavedFormCheck(this, ['form']);
    this.isBulkOperation = this.data.selectedObjects.length > 1;
    if (this.data.editCheckYear) {
      this.form.get('triggerEventCheckYear')!.addValidators([IczValidators.required()]);
      this.form.get('triggerEventCheckYear')!.updateValueAndValidity();
    } else {
      this.form.get('yearOfRetentionPeriodStart')!.addValidators([IczValidators.required()]);
      this.form.get('yearOfRetentionPeriodStart')!.updateValueAndValidity();
    }
  }

  submit() {
    const formData = this.form.getRawValue();
    const reqData = this.data.editCheckYear ? {triggerEventCheckYear: formData.triggerEventCheckYear} : {yearOfRetentionPeriodStart: formData.yearOfRetentionPeriodStart};
    const requestData: BulkTriggerEventUpdateDto = {...reqData, operationEntityDtos: this.data.selectedObjects.map(obj => ({entityType: isDocumentEntity(obj.entityType!) ? EntityType.DOCUMENT : EntityType.FILE, id: obj.id!}))};

    this.loadingService.doLoading(
      this.apiSettlementService.settlementUpdateTriggerYears({body: requestData}),
      this
    ).subscribe(_ => {
      if (this.isBulkOperation) {
        this.documentToastService.dispatchBulkOperationStartedToast(this.data.editCheckYear ? DocumentToastType.BULK_RETENTION_TRIGGER_CHECK_YEAR_UPDATED : DocumentToastType.BULK_RETENTION_TRIGGER_YEAR_UPDATED, {
          [InternalNotificationKey.COUNT]: this.data.selectedObjects.length
        });
      } else {
        if (isDocumentEntity(this.data.selectedObjects[0].entityType)) {
          this.documentToastService.dispatchDocumentInfoToast(this.data.editCheckYear ? DocumentToastType.DOCUMENT_RETENTION_TRIGGER_CHECK_YEAR_UPDATED : DocumentToastType.DOCUMENT_RETENTION_TRIGGER_YEAR_UPDATED, {
            [InternalNotificationKey.DOCUMENT_ID]: this.data.selectedObjects[0].id!,
            [InternalNotificationKey.DOCUMENT_SUBJECT]: this.data.selectedObjects[0].subject
          });
        } else {
          this.fileToastService.dispatchFileInfoToast(this.data.editCheckYear ? FileToastType.FILE_RETENTION_TRIGGER_CHECK_YEAR_UPDATED : FileToastType.FILE_RETENTION_TRIGGER_YEAR_UPDATED, {
            [InternalNotificationKey.FILE_ID]: this.data.selectedObjects[0].id!,
            [InternalNotificationKey.FILE_SUBJECT]: this.data.selectedObjects[0].subject
          });
        }
      }
      this.form.markAsPristine();
      this.modalRef.close(true);
    });
  }

  cancel() {
    this.modalRef.close(null);
  }

}
